import { type ErrorCode, isRetryableCode } from './error-codes.js';
import type { ErrorEnvelope } from './types.js';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly requestId: string;
  readonly details: Record<string, unknown> | undefined;
  readonly retryable: boolean;
  readonly retryAfterMs: number | null;

  constructor(opts: {
    code: ErrorCode;
    message: string;
    httpStatus: number;
    requestId: string;
    details?: Record<string, unknown>;
    retryAfterMs?: number | null;
    // Override per casi dove il code suggerirebbe retryable=true ma il
    // contesto non lo permette (es. malformed JSON 2xx interpretato come
    // internal_error → comunque terminal: il server ha risposto OK ma
    // il payload è rotto, retry non aiuta).
    retryable?: boolean;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.httpStatus = opts.httpStatus;
    this.requestId = opts.requestId;
    this.details = opts.details;
    this.retryable = opts.retryable ?? isRetryableCode(opts.code);
    this.retryAfterMs = opts.retryAfterMs ?? null;
  }
}

export class NetworkError extends Error {
  readonly retryable = true;
  readonly causeValue: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    this.causeValue = cause;
  }
}

const ERROR_CODES_SET: ReadonlySet<string> = new Set<ErrorCode>([
  'unauthorized',
  'forbidden',
  'not_found',
  'gone_expired',
  'conflict_max_presets',
  'rate_limited',
  'invalid_payload',
  'invalid_format',
  'internal_error',
  // Phase 9 SEZ 5 device tracking:
  'device_required',
  'device_revoked',
  // Phase 10 Task 1.2 auth endpoints:
  'email_already_exists',
  'email_not_verified',
  'account_suspended',
  'invalid_credentials',
  'token_invalid',
  // Phase 10 Task 8 legacy migration:
  'legacy_auth_expired',
  'already_migrated',
  'studio_missing_contact_email',
]);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  if (!isPlainRecord(value)) return false;
  const envelope = value as { error?: unknown };
  if (!isPlainRecord(envelope.error)) return false;
  const err = envelope.error as {
    code?: unknown;
    message?: unknown;
    request_id?: unknown;
    details?: unknown;
  };
  if (typeof err.code !== 'string' || !ERROR_CODES_SET.has(err.code)) return false;
  if (typeof err.message !== 'string') return false;
  if (typeof err.request_id !== 'string') return false;
  // details è opzionale: se presente DEVE essere plain object (no array, no scalar).
  if (err.details !== undefined && !isPlainRecord(err.details)) return false;
  return true;
}

export function parseErrorEnvelope(payload: unknown): ErrorEnvelope | null {
  return isErrorEnvelope(payload) ? payload : null;
}

const RETRY_AFTER_MIN_SEC = 1;
const RETRY_AFTER_MAX_SEC = 60;

// Backend `oniri-viewer-api/src/middleware/rate-limit.ts:67` emits Retry-After
// as delta-seconds via String(retryAfterSec). HTTP-date format is RFC-allowed
// fallback (not currently used by backend) — we accept it for robustness.
export function parseRetryAfter(headerValue: string | null): number | null {
  if (headerValue === null || headerValue.length === 0) return null;
  const trimmed = headerValue.trim();
  const asSeconds = Number(trimmed);
  if (Number.isFinite(asSeconds) && asSeconds >= 0) {
    const clamped = Math.min(Math.max(asSeconds, RETRY_AFTER_MIN_SEC), RETRY_AFTER_MAX_SEC);
    return clamped * 1000;
  }
  // HTTP-date fallback (RFC 7231) — backend non lo emette ma robustezza future.
  const dateMs = Date.parse(trimmed);
  if (Number.isFinite(dateMs)) {
    const deltaMs = dateMs - Date.now();
    if (deltaMs <= 0) return RETRY_AFTER_MIN_SEC * 1000;
    return Math.min(deltaMs, RETRY_AFTER_MAX_SEC * 1000);
  }
  return null;
}

export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}
